'use client';

import { useId } from 'react';
import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiInformationLine } from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { dur, ease, spring, springFade, stagger } from '@/lib/motion';
import { hl } from '@/lib/rich-text';

/**
 * "Ajanın yapmadıkları" — sayfanın son içerik bölümü, kapanış CTA'sının üstü.
 *
 * Sayfa buraya kadar üç şey yaptı: gösterdi, kapsamı çizdi, dayanağını
 * anlattı. Kalan tek soru "peki neyi yapmıyor" — ve bu sayfada onu SÖYLEMEK
 * satmaktan daha değerli: reklam hesabını açacak kişi abartılmış bir vaadin
 * bedelini kendi parasıyla ödüyor.
 *
 * ── Biçim /about'un "Ne yapmıyoruz" bölümünden ────────────────────────────
 *
 * Her satır bir ÇİFT: solda reddedilen şey, sağda onun yerine olan. Tek
 * sütunlu bir "yapmıyoruz" listesi cümleyi yarım bırakır; ürünün duruşu
 * sağdaki yarıda. Ret tarafı daha hafif mürekkepte — sayfanın söylediği şey
 * cevap tarafında (aynı yapı: components/about-limits.tsx).
 *
 * Etiketler ("Yapmaz" / "Bunun yerine") gerçek metin: hangi yarının ne olduğu
 * yalnızca renkten okunsaydı bilgi tek bir taşıyıcıya kalırdı (DESIGN.md §1).
 *
 * ── Hareket ───────────────────────────────────────────────────────────────
 *
 * Satır ayraçları soldan sağa çiziliyor (scaleX), metinden hemen ÖNCE:
 * liste kendini yukarıdan aşağı kuruyor gibi duruyor. Süs değil, okuma
 * sırasının kendisi.
 */

const ROWS = ['budget', 'create', 'promise', 'scope'] as const;

const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base } },
};

const copyV: Variants = {
  hidden: { opacity: 0, y: 10, filter: 'blur(5px)' },
  show: { opacity: 1, y: 0, filter: 'blur(0px)', transition: springFade(spring.default) },
};

const listV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.05 } },
};

const rowV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const ruleV: Variants = {
  hidden: { scaleX: 0 },
  show: { scaleX: 1, transition: { duration: dur.base, ease: ease.out } },
};

const partV: Variants = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

export function AgentBounds() {
  const t = useTranslations('featuresAgent.bounds');
  const uid = useId();

  return (
    <section className="bounds" aria-labelledby={`${uid}-title`}>
      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="bounds-inner">
            <div className="bounds-layout">
              <motion.div className="bounds-aside" variants={headV} initial={false} animate="show">
                <motion.p className="bounds-eyebrow" variants={copyV}>
                  {t('eyebrow')}
                </motion.p>
                <motion.h2 className="bounds-title" id={`${uid}-title`} variants={copyV}>
                  {t.rich('title', { hl })}
                </motion.h2>
                <motion.p className="bounds-lead" variants={copyV}>
                  {t('lead')}
                </motion.p>
                <motion.p className="bounds-note" variants={copyV}>
                  <RiInformationLine size={16} aria-hidden="true" />
                  {t('note')}
                </motion.p>
              </motion.div>

              <motion.ul className="bounds-list" variants={listV} initial={false} animate="show">
                {ROWS.map((key) => (
                  <motion.li key={key} className="bounds-row" variants={rowV}>
                    <motion.span className="bounds-rule" variants={ruleV} aria-hidden="true" />

                    <motion.div variants={partV}>
                      <span className="bounds-tag">{t('notTag')}</span>
                      <h3 className="bounds-not">{t(`items.${key}.not`)}</h3>
                    </motion.div>

                    <motion.div variants={partV}>
                      <span className="bounds-tag bounds-tag--yes">{t('yesTag')}</span>
                      <p className="bounds-instead">{t(`items.${key}.instead`)}</p>
                    </motion.div>
                  </motion.li>
                ))}
              </motion.ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
